import { Link, useLocation } from "react-router-dom";
import { SiteHeader } from "../components/platform/PlatformChrome";
import { Footer } from "../components/landing/Footer";
import LibraryFeatured from "./dashboard/library/LibraryFeatured";
import LibraryDocuments from "./dashboard/library/LibraryDocuments";
import LibraryVideos from "./dashboard/library/LibraryVideos";
import LibrarySeries from "./dashboard/library/LibrarySeries";
import LibraryResources from "./dashboard/library/LibraryResources";
import LibraryIntroduction from "./dashboard/library/LibraryIntroduction";

/**
 * Public Library — `/library`, `/library/documents`, `/library/videos`,
 * `/library/series`, `/library/resources`, `/library/introduction`. Mirrors the dashboard
 * `/dashboard/library` shell exactly: same tab list, same content
 * components. The only difference is the surrounding chrome (Nav/Footer
 * for public; the dashboard shell for the authenticated surface) and the
 * slightly richer header type — display serif for the public landing
 * register, the lighter `page-title` for the dashboard register.
 *
 * Document detail at `/library/:slug` is its own route (DocumentPage),
 * which is why the `/library/...` tab routes are explicit and registered
 * before the catch-all slug route in App.tsx.
 */

type LibraryTab = "featured" | "introduction" | "documents" | "videos" | "series" | "resources";

const TABS: ReadonlyArray<{ to: string; label: string; key: LibraryTab }> = [
  { to: "/library", label: "Featured", key: "featured" },
  { to: "/library/introduction", label: "Introduction", key: "introduction" },
  { to: "/library/documents", label: "Documents", key: "documents" },
  { to: "/library/videos", label: "Videos", key: "videos" },
  { to: "/library/series", label: "Series", key: "series" },
  { to: "/library/resources", label: "Resources", key: "resources" },
];

const PATH_TO_TAB: Record<string, LibraryTab> = {
  "/library/introduction": "introduction",
  "/library/documents": "documents",
  "/library/videos": "videos",
  "/library/series": "series",
  "/library/resources": "resources",
};

interface PublicLibraryProps {
  tab?: LibraryTab;
}

export default function PublicLibrary({ tab = "featured" }: PublicLibraryProps) {
  const location = useLocation();
  const active: LibraryTab = PATH_TO_TAB[location.pathname] ?? tab;

  return (
    <>
      <SiteHeader />
      <main className="pt-28 sm:pt-32 pb-20 sm:pb-24">
        <div className="container-edit max-w-[1280px]">
          <header className="mb-9">
            <p className="eyebrow mb-3">Library</p>
            <h1 className="font-display text-[clamp(2.5rem,6vw,4.25rem)] leading-[1.04] tracking-[-0.02em]">
              Read, watch,{" "}
              <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
                return
              </span>
              .
            </h1>
            <p className="text-[1.1rem] text-[var(--color-ink-soft)] max-w-[60ch] mt-5">
              Documents, recordings, classes, and a curated index of the wider
              Sanskrit and Vedic study world. The umbrella for everything you
              can come back to.
            </p>
          </header>

          <nav
            className="flex flex-wrap gap-x-4 gap-y-2 border-b border-[var(--color-rule)] mb-9"
            aria-label="Library sections"
          >
            {TABS.map((t) => (
              <Link
                key={t.to}
                to={t.to}
                className={`subtab ${active === t.key ? "is-active" : ""}`}
              >
                {t.label}
              </Link>
            ))}
          </nav>

          {active === "featured" && <LibraryFeatured />}
          {active === "introduction" && <LibraryIntroduction />}
          {active === "documents" && <LibraryDocuments />}
          {active === "videos" && <LibraryVideos />}
          {active === "series" && <LibrarySeries />}
          {active === "resources" && <LibraryResources />}
        </div>
      </main>
      <Footer />
    </>
  );
}
